import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, ReplaySubject, asyncScheduler } from 'rxjs';
import { delay, filter, first, map, throttleTime } from 'rxjs/operators';
import { LanguageMap, Mote } from '../models';
import { TourTagLabel } from '../models/tour-tag-label';
import { FilterCategory, FoodFilter, GenreFilter, GeoFilter, LangFilter, PriceFilter } from '../models/tours-filter';
import { ResolverItemStream } from './data-resolve.service';
import { AutoSub, AutoSubOnDestroy } from './auto-sub';
import { LanguageService } from './language.service';
import { MoteService } from './mote.service';
import { GeoTourTag, TourTag } from '../models/tour-tags';

import { CitySearchResults } from '@motes-sdk/models/db-types';
import { SupabaseService } from './supabase.service';

export type TourMap = { [category: string]: Mote[] };
export enum TourView {
    EXPERIENCE_PAGE = "EXPERIENCE_PAGE",
    EXPERIENCE_MAP = "EXPERIENCE_MAP",
    TOUR_PAGE = "TOUR_PAGE",
    TOUR_MAP = "TOUR_MAP",
    HIDDEN = "HIDDEN"
}

@Injectable({ providedIn: 'root' })

@AutoSubOnDestroy()
export class ToursFilterService {

    public allTours: Mote[] = [];
    public filteredTours: Mote[] = [];
    public filters: FilterCategory[] = [new LangFilter, new GeoFilter, new GenreFilter, new FoodFilter, new PriceFilter];
    public searchInput: string = '';
    public optionsChosen: number = 0;
    public filtersOpen: boolean = false;
    private isInitialized: boolean = false;
    public allToursChange: ReplaySubject<Mote[]> = new ReplaySubject<Mote[]>(1);
    public shownToursChange: ReplaySubject<{ tourResults: Mote[], cityResults: CitySearchResults}> = new ReplaySubject(1);
    private currentUrl: string;
    private previousUrl: string;

    private cityResults: CitySearchResults = [];
    public citySearchInputSubject: ReplaySubject<string> = new ReplaySubject<string>(1);
    // Keep track of the current city search query sequence number to avoid old responses on response delay 
    private citySearchSeqNo: number = 0;

    constructor(@Inject(LOCALE_ID) private locale: string, private moteService: MoteService, private router: Router, private db: SupabaseService) {
        AutoSub(this).reg['urlSub'] = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((ev: NavigationEnd) => {
            this.previousUrl = this.currentUrl;
            this.currentUrl = ev.urlAfterRedirects;
        });

        AutoSub(this).reg['citySearchSub'] = this.citySearchInputSubject.pipe(throttleTime(300, asyncScheduler, { leading: false, trailing: true })).subscribe( text => this.searchCities(text) );
    }

    public getPreviousUrl(): string {
        return this.previousUrl;
    }

    public async init(route: ActivatedRoute = null) {
        if(this.isInitialized) return;
        this.isInitialized = true;

        // Init without data resolve
        if(!route) return this.initWithoutResolve();

        // Init with data resolve
        let toursResolver = route.data.pipe(first(), map(data => data.tours)) as Observable<ResolverItemStream<Mote[]>>;
        let resolvedToursData = await toursResolver.toPromise();
        this.allTours = resolvedToursData.items;
        this.onAllToursChange();
        // Get tours from data resolver and switch to live stream
        AutoSub(this).reg['toursSub'] = resolvedToursData.stream.pipe(delay(0)).subscribe(async tours => {
            this.allTours = tours;
            this.onAllToursChange();
        });
    }

    private async initWithoutResolve() {
        AutoSub(this).reg['toursSub'] = this.moteService.watchPublishedTourMotes().subscribe(async tours => {
            this.allTours = tours;
            this.onAllToursChange();
        });
    }

    /**
     * Emit change of all tours
     */
    public onAllToursChange() {
        this.allToursChange.next(this.allTours);
        this.onShownToursChange();
    }

    /**
     * Emit change of shown tours
     */
    public onShownToursChange() {
        let shownTours = this.getShownTours();
        this.shownToursChange.next({ tourResults: shownTours, cityResults: this.cityResults });
    }

    public getShownTours(): Mote[] {
        return this.optionsChosen > 0 || this.searchInput ? this.filteredTours : LanguageService.filterMotesByLocale(this.allTours, this.locale);
    }

    /**
     * Watch shown tours (either all or filtered by filters or search)
    */
    public watchShownTours(): Observable<{ tourResults: Mote[], cityResults: CitySearchResults }> {
        return this.shownToursChange.asObservable();
    }

    /**
     * Watch all published tours
     */
    public watchAllTours(): Observable<Mote[]> {
        return this.allToursChange.asObservable();
    }

    /**
     * Watch shown tours (either all or filtered by filters or search)
     */
    public watchSearchResults(): Observable<{ tourResults: Mote[], cityResults?: CitySearchResults }> {
        return this.shownToursChange.asObservable();
    }

    /**
     * Unselect all filter options and empty filteredTours
     */
    public clearFilters() {
        this.filters = [new LangFilter, new GeoFilter, new GenreFilter, new FoodFilter, new PriceFilter];
        if(this.optionsChosen) {
            this.optionsChosen = 0;
            this.updateFilteredTours([]);
        }
    }

    /**
     * Empty search bar and filteredTours
     */
    public clearSearch() {
        this.searchInput = '';
        this.cityResults = [];
        this.updateFilteredTours([]);
    }

    /**
     * Update filteredTours by piping allTours through filters
     */
    public filterTours() {
        // Clear search bar
        this.searchInput = '';
        this.optionsChosen = 0;
        let filteredTours = [...this.allTours].filter(tour => !tour.hideFromSearch);
        this.filters.forEach((filter) => {
            filteredTours = filter.filterTours(filteredTours, this.locale);
            this.optionsChosen += filter.options.reduce((sum, option) => sum + (option.isChosen ? 1 : 0), 0);
        });
        this.updateFilteredTours(filteredTours);
    }

    /**
     * Update filteredTours by matching allTours with the search bar input
     */
    public searchTours() {
        if(!this.searchInput) return this.clearSearch();

        this.clearFilters();
        let filteredTours = this.allTours.filter((tour) => {
            let tags = tour?.tags ? tour.tags.split(',').map((tag) => (tag.trim() in TourTagLabel) ? TourTagLabel[tag.trim()] : tag).join(',') : '';
            let lang = LanguageMap[tour.language]?.label || "Unknown language";
            let searchString = tour.title.concat(tour.previewText).concat(tags).concat(lang);
            // Search for first world city in result list (this gives better results than taking all cities in results)
            const inCitySearchResult = this.cityResults[0]?.id == tour.worldCityId;
            return this.normalizeString(searchString).includes(this.normalizeString(this.searchInput)) || inCitySearchResult;
        });
        // Remove language duplicates
        filteredTours = LanguageService.filterMotesByLocale(filteredTours, this.locale).filter(tour => !tour.hideFromSearch);
        this.updateFilteredTours(filteredTours);
    }


    public updateCitySearchQuery(query: string) {
        this.citySearchInputSubject.next(query);
    }

    /**
     * Update cityList based on search bar input
     */
    private async searchCities(query: string) {
        if (!query) return this.clearSearch();

        this.citySearchSeqNo = this.citySearchSeqNo + 1;

        const cli = this.db.getAnonSupabaseClient();
        const { data: response, error } = await cli.rpc('search_city_v2', {
            query_text: query,
            match_count: 5
        }).single();
        let cityResults = response?.results || [];

        // Crop list to contain fewer results to make a cleaner view if there is a clear result
        if(cityResults.length > 0 &&  cityResults[0].similarity > 0.55) {
            cityResults = cityResults.filter( city => city.similarity > 0.55).slice(0,2);
        }
        else if (cityResults.length > 0) {
            cityResults = cityResults.slice(0,2);
        }

        this.cityResults = cityResults;
        this.searchTours();

        // Abort old results to avoid inconsistent view state
        if(!this.searchInput || (response?.seq_no || -1) < this.citySearchSeqNo - 1 ) return;
        this.citySearchSeqNo = response.seq_no;

        this.shownToursChange.next({ tourResults: this.filteredTours, cityResults });
    }

    private normalizeString(s: string): string {
        return s.toLocaleLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
    }

    private updateFilteredTours(filteredTours: Mote[]) {
        this.filteredTours = filteredTours;
        // Update shown tours
        let shownTours = this.getShownTours();
        this.shownToursChange.next({ tourResults: shownTours, cityResults: this.cityResults });
    }

    /**
     * Sort tours into a map of categories
     */
    public categorizeTours(tours: Mote[], categories: TourTag[]): TourMap {
        let tourMap = {};
        categories.forEach((category) => tourMap[category] = []);

        for(let tour of tours) {
            let categorized: boolean = false;
            let tags = tour?.tags ? tour.tags.replace(/\s/g, '').split(',') : [];

            // Add to categories based on tags
            for(let category of categories) {
                if(!tags.includes(category)) continue;
                tourMap[category].push(tour);
                categorized = true;
            }

            if(!categorized) tourMap[GeoTourTag.other].push(tour);

        }

        // Remove duplicate tours from each category and resort by order
        categories.forEach((category) => tourMap[category] = LanguageService.filterMotesByLocale(tourMap[category], this.locale).sort((a: Mote, b: Mote) => a.order - b.order));

        return tourMap;
    }
}
